import { AnimatePresence, motion } from 'framer-motion';
import { Camera, Loader2, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { compressBase64Image, compressImageToBase64 } from '@/utils/image-compression';

interface ProfilePhotoUploadProps {
  onUpload: (base64Image: string) => Promise<void>;
  onClose: () => void;
}

export function ProfilePhotoUpload({ onUpload, onClose }: ProfilePhotoUploadProps) {
  const [mode, setMode] = useState<'select' | 'camera' | 'preview'>('select');
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const webcamRef = useRef<Webcam>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const compressedBase64 = await compressImageToBase64(file, {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 800,
        quality: 0.8,
      });
      setPreview(compressedBase64);
      setMode('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process image');
    } finally {
      setUploading(false);
    }
  };

  const handleCameraCapture = async () => {
    if (!webcamRef.current) {
      setError('Camera not initialized');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) {
        throw new Error('Failed to capture image');
      }

      const compressedBase64 = await compressBase64Image(imageSrc, {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 800,
        quality: 0.8,
      });

      setPreview(compressedBase64);
      setMode('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to capture image');
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!preview) return;

    setUploading(true);
    setError(null);

    try {
      await onUpload(preview);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleRetake = () => {
    setPreview(null);
    setMode('select');
    setError(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="relative w-full max-w-2xl bg-white dark:bg-neolab-forest rounded-3xl shadow-2xl border border-neolab-green/10 dark:border-white/10 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-neolab-green/5 dark:border-white/5 flex items-center justify-between bg-neolab-green/[0.02] dark:bg-white/5">
          <h2 className="text-xl font-black text-slate-900 dark:text-white">
            Upload Profile Photo
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-neolab-green/10 dark:hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          </button>
        </div>

        <div className="p-6">
          <AnimatePresence mode="wait">
            {mode === 'select' && (
              <motion.div
                key="select"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex flex-col items-center justify-center gap-4 p-8 rounded-2xl bg-neolab-green/5 dark:bg-white/5 border-2 border-dashed border-neolab-green/20 dark:border-white/20 hover:border-neolab-green/40 dark:hover:border-white/40 transition-all cursor-pointer disabled:opacity-50"
                  >
                    <div className="p-4 rounded-xl bg-neolab-green/10 dark:bg-white/10">
                      <Upload className="w-8 h-8 text-neolab-green" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-black text-slate-900 dark:text-white mb-1">
                        Upload from Device
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Choose an image file
                      </p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setMode('camera')}
                    disabled={uploading}
                    className="flex flex-col items-center justify-center gap-4 p-8 rounded-2xl bg-blue-500/5 dark:bg-blue-500/10 border-2 border-dashed border-blue-500/20 dark:border-blue-500/30 hover:border-blue-500/40 dark:hover:border-blue-500/50 transition-all cursor-pointer disabled:opacity-50"
                  >
                    <div className="p-4 rounded-xl bg-blue-500/10 dark:bg-blue-500/20">
                      <Camera className="w-8 h-8 text-blue-500" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-black text-slate-900 dark:text-white mb-1">
                        Capture from Camera
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Take a new photo</p>
                    </div>
                  </button>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </motion.div>
            )}

            {mode === 'camera' && (
              <motion.div
                key="camera"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-4"
              >
                <div className="relative rounded-2xl overflow-hidden bg-slate-900">
                  <Webcam
                    ref={webcamRef}
                    audio={false}
                    screenshotFormat="image/jpeg"
                    videoConstraints={{
                      width: 1280,
                      height: 720,
                      facingMode: 'user',
                    }}
                    className="w-full h-auto"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setMode('select')}
                    className="flex-1 px-6 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-black text-sm hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleCameraCapture}
                    disabled={uploading}
                    className="flex-1 px-6 py-3 rounded-xl bg-neolab-green text-white font-black text-sm shadow-lg shadow-green-500/20 hover:bg-neolab-green/90 transition-all cursor-pointer disabled:opacity-50"
                  >
                    {uploading ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Processing...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Camera className="w-4 h-4" />
                        Capture
                      </span>
                    )}
                  </button>
                </div>
              </motion.div>
            )}

            {mode === 'preview' && preview && (
              <motion.div
                key="preview"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-4"
              >
                <div className="relative rounded-2xl overflow-hidden bg-slate-100 dark:bg-slate-800">
                  <img
                    src={preview}
                    alt="Preview"
                    className="w-full h-auto max-h-96 object-contain mx-auto"
                  />
                </div>

                {error && (
                  <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800">
                    <p className="text-sm text-rose-700 dark:text-rose-300 font-bold">{error}</p>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleRetake}
                    disabled={uploading}
                    className="flex-1 px-6 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-black text-sm hover:bg-slate-200 dark:hover:bg-slate-700 transition-all cursor-pointer disabled:opacity-50"
                  >
                    Retake
                  </button>
                  <button
                    type="button"
                    onClick={handleUpload}
                    disabled={uploading}
                    className="flex-1 px-6 py-3 rounded-xl bg-neolab-green text-white font-black text-sm shadow-lg shadow-green-500/20 hover:bg-neolab-green/90 transition-all cursor-pointer disabled:opacity-50"
                  >
                    {uploading ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Uploading...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Upload className="w-4 h-4" />
                        Upload Photo
                      </span>
                    )}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
